/**
 * Phân quyền UI cho các trang admin có role bị giới hạn thao tác:
 *   - Quản lý Account                  (/admin/account-manager)
 *   - Quản lý giao dịch nạp tiền        (/admin/recharge-manager)
 *   - Danh sách Yêu cầu Xử lý Bài thu   (/admin/recording-ai)
 *
 * Mô hình: mỗi role có một DANH SÁCH CHẶN (blacklist) — được làm mọi thứ trên
 * trang TRỪ các action liệt kê. Vì là blacklist nên action mới thêm sau này mặc
 * định ĐƯỢC phép; muốn chặn thì phải bổ sung vào danh sách tương ứng.
 *
 * Các role không có mặt trong DENIED_ACTIONS_BY_ROLE giữ nguyên hành vi cũ
 * (không bị siết thêm gì).
 *
 * Đây chỉ là tầng ẩn UI cho đỡ rối mắt. Quyền thật do backend quyết định bằng
 * @RolesAllowed (ikaraGAE) hoặc @PreAuthorize (ikara-parent) trên từng endpoint
 * — sửa bên này thì phải sửa bên đó, và ngược lại.
 */

export const ROLE_PART_TIME = "CUSTOMER-SUPPORT-PART-TIME";
export const ROLE_KTV = "KTV";

/**
 * Thao tác mà CSKH bán thời gian KHÔNG được phép, khớp với các endpoint không
 * khai báo role này trong @RolesAllowed ở backend.
 */
const PART_TIME_DENIED_ACTIONS = [
    "banAll",        // Khóa all tài khoản      -> /rest/admin/ban
    "unban",         // Mở khóa tài khoản       -> /rest/admin/unban
    "banOnly",       // Hạn chế tài khoản này   -> /rest/admin/banOnlyUser
    "banFeature",    // Chặn tính năng          -> /rest/admin/banUserFeature
    "addRelated",    // Add Related             -> /rest/admin/addRelatedUser
    "addScore",      // Add Score               -> /rest/admin/addScore
    "addVipScore",   // Add Vip Score           -> /rest/admin/addVipScore
    "setPassword",   // Set mật khẩu            -> /rest/admin/setUidPassword
    "addIcoin",      // Thêm Icoin              -> /rest/admin/addIcoin

    // Tab Giỏ quà: chỉ được xem, không thêm/sửa/xoá quà.
    "editUserGift",  // Thêm/Sửa/Xoá quà -> /rest/gifts/getDetail, /updateUserGift, /deleteUserGift

    // Trang Quản lý giao dịch nạp tiền (/admin/recharge-manager):
    // được bấm "Xử lí" giao dịch lỗi, KHÔNG được hoàn tiền.
    "refundTopUp",   // Refund -> /mc-user/referral/web/refund-top-up
];

/**
 * Thao tác mà KTV (kỹ thuật viên) KHÔNG được phép.
 * KTV dùng được mọi thao tác trên trang Danh sách Yêu cầu Xử lý Bài thu trừ hủy.
 */
const KTV_DENIED_ACTIONS = [
    "cancelRecordingVip", // Hủy xử lý -> /rest/recordingvip/cancel
];

/** Blacklist theo từng role. Role không có trong map này thì không bị siết. */
const DENIED_ACTIONS_BY_ROLE = {
    [ROLE_PART_TIME]: PART_TIME_DENIED_ACTIONS,
    [ROLE_KTV]: KTV_DENIED_ACTIONS,
};

/** Các role vốn đã có toàn quyền trên trang Quản lý Account từ trước. */
const FULL_ACCESS_ROLES = ["MANAGER", "ICOIN-MANAGER", "CUSTOMER-SUPPORTER"];

/**
 * Trả về true nếu user được phép thực hiện `action`.
 *
 * Lưu ý: hàm này chỉ siết với role có mặt trong DENIED_ACTIONS_BY_ROLE. Các role
 * khác giữ nguyên hành vi cũ (backend vẫn là nơi chặn thật), để thay đổi không
 * vô tình khóa bớt quyền của những role đang chạy ổn định.
 */
export function canDoAccountAction(user, action) {
    if (user == null) return false;

    const denied = DENIED_ACTIONS_BY_ROLE[user.role];
    if (denied) {
        return !denied.includes(action);
    }

    return true;
}

/** Role part-time không được xem tab Message (chỉ MANAGER mới được). */
export function canViewMessageTab(user) {
    return user != null && user.role === "MANAGER";
}

/** Role đã có toàn quyền từ trước — dùng khi cần phân biệt rõ với part-time. */
export function hasFullAccountAccess(user) {
    return user != null && FULL_ACCESS_ROLES.includes(user.role);
}
